import { SHELL_HEADER_HEIGHT } from "./pifLayout";
import { PifFormValues } from "./types";

/**
 * Where a validation error sends the keyboard.
 *
 * Every gap the form reports (pifValidation) carries the DOM id of the control that answers it, so
 * clicking the error lands the caret in that field instead of at the top of its section. The ids
 * are built here rather than written out at both ends, so the check and the control cannot drift.
 */

/** e.g. "firstName" → "pif-field-firstName". Keyed on PifFormValues, so a rename breaks the build. */
export const pifFieldId = (key: keyof PifFormValues): string => `pif-field-${key}`;

/** A dynamic (EAV) field, which has no PifFormValues key — its identity is the catalogue fieldKey. */
export const pifEavFieldId = (fieldKey: string): string => `pif-field-eav-${fieldKey}`;

/**
 * The bridged EAV keys (types.ts PIF_BRIDGED_FIELD_KEYS) mapped to the bespoke Position / Pay
 * control that captures them. Needed because a rejection from save-answers names the EAV key,
 * while what the user has to fix is that control — PifSectionDynamic doesn't render these.
 */
const BRIDGED_FIELD_CONTROL: Record<string, keyof PifFormValues> = {
  requested_rate: "rate",
  desired_start_date: "startDate",
  department_notes: "notes",
  hours_per_week: "hours",
  rate_type: "rateTypeId",
  service: "serviceId",
  client: "clientId",
  reports_to: "reportsToId",
  employment_type: "empType",
};

/** The control holding an answer the API named by EAV field key, bridged or dynamic. */
export const pifAnswerFieldId = (fieldKey: string): string => {
  const bridged = BRIDGED_FIELD_CONTROL[fieldKey];
  return bridged ? pifFieldId(bridged) : pifEavFieldId(fieldKey);
};

/**
 * Room kept above the control for the label that names it: focus is worthless if the question
 * ("Rate type *") is hidden behind the sticky header and only the empty box shows.
 */
const LABEL_CLEARANCE = 34;

/**
 * Scrolls a control clear of the sticky headers and focuses it. Answers false when the id isn't on
 * screen — a section can be conditional (the licence fields, the client card), and the caller then
 * falls back to jumping to the section.
 *
 * @param stickyHeaderHeight the workspace's own sticky header, measured; the shell's is a constant.
 */
export const focusPifField = (id: string, stickyHeaderHeight: number): boolean => {
  const el = document.getElementById(id);
  if (!el) return false;

  const top = el.getBoundingClientRect().top + window.scrollY - SHELL_HEADER_HEIGHT - stickyHeaderHeight - LABEL_CLEARANCE;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  // preventScroll, or the browser's own instant scroll-into-view on focus cancels the smooth
  // scroll above and leaves the field sitting under the sticky header.
  el.focus({ preventScroll: true });
  return true;
};
